import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { GoogleAuthButton } from "@/components/GoogleAuthButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      toast.success("Welcome back!");
      const role = data.user.role;
      const dest = role === "admin" || role === "sub_admin" ? "/dashboard/admin" : role === "influencer" ? "/dashboard/influencer" : role === "partner" ? "/dashboard/partner" : "/dashboard/track";
      navigate(dest);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <div className="w-full max-w-sm">
        <Link to="/"><Logo /></Link>
        <h1 className="font-display text-3xl font-bold text-[#0B2447] mt-10">Welcome back</h1>
        <p className="text-slate-500 mt-2">Log in to continue your application.</p>

        <div className="mt-8">
          <GoogleAuthButton label="Continue with Google" />
        </div>
        <div className="flex items-center gap-3 my-5">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-xs uppercase tracking-wider text-slate-400">or with email</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <Label className="text-slate-700">Email Address</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email" className="mt-1.5 p-3" placeholder="you@example.com" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-slate-700">Password</Label>
              <Link to="/forgot-password" className="text-xs text-[#0B2447] font-semibold underline" data-testid="forgot-password-link">Forgot password?</Link>
            </div>
            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password" className="mt-1.5 p-3" placeholder="••••••••" />
          </div>
          <Button type="submit" disabled={loading} data-testid="login-submit"
            className="w-full bg-[#0B2447] hover:bg-[#153461] py-6 text-base">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log In"}
          </Button>
        </form>
        <p className="mt-6 text-sm text-slate-500">
          No account yet?{" "}
          <Link to="/register" className="text-[#0B2447] font-semibold underline">Create one</Link>
        </p>
      </div>
    </AuthShell>
  );
}

export function AuthShell({ children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          src="https://images.pexels.com/photos/37898351/pexels-photo-37898351.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940"
          alt="Students"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0B2447]/80" />
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="relative h-full flex flex-col justify-end p-12 text-white"
        >
          <p className="font-display text-3xl font-bold leading-tight max-w-md">
            "Getting into university shouldn't be complicated. We make it simple."
          </p>
          <p className="mt-4 text-white/70">Your Varsity Application</p>
        </motion.div>
      </div>
      <div className="flex items-center justify-center p-8 bg-[#F8F9FA]">{children}</div>
    </div>
  );
}
